import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import Versicherungsvergleich from "@/pages/Versicherungsvergleich";
import Immobilien from "@/pages/Immobilien";
import PropertyDetail from "@/pages/PropertyDetail";
import Anfrage from "@/pages/Anfrage";
import UeberUns from "@/pages/UeberUns";
import Kontakt from "@/pages/Kontakt";
import Terminbuchung from "@/pages/Terminbuchung";
import Schadenmeldung from "@/pages/Schadenmeldung";
import { Impressum, Datenschutz } from "@/pages/Legal";
import { AGB, Erstinformation, CookieSettings } from "@/pages/LegalExtra";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Rechner from "@/pages/Rechner";
import Ratgeber from "@/pages/Ratgeber";
import BlogDetail from "@/pages/BlogDetail";
import FAQ from "@/pages/FAQ";
import Downloads from "@/pages/Downloads";

// Lazy-loaded (Code-Splitting) für schnelleres initiales Laden der öffentlichen Seite
const CrmLayout = lazy(() => import("@/pages/crm/CrmLayout"));
const Dashboard = lazy(() => import("@/pages/crm/Dashboard"));
const Leads = lazy(() => import("@/pages/crm/Leads"));
const Kunden = lazy(() => import("@/pages/crm/Kunden"));
const KundeDetail = lazy(() => import("@/pages/crm/KundeDetail"));
const Policen = lazy(() => import("@/pages/crm/Policen"));
const Termine = lazy(() => import("@/pages/crm/Termine"));
const Schaeden = lazy(() => import("@/pages/crm/Schaeden"));
const Dokumente = lazy(() => import("@/pages/crm/Dokumente"));
const Erinnerungen = lazy(() => import("@/pages/crm/Erinnerungen"));
const ImmobilienAdmin = lazy(() => import("@/pages/crm/ImmobilienAdmin"));
const Aufgaben = lazy(() => import("@/pages/crm/Aufgaben"));
const Statistik = lazy(() => import("@/pages/crm/Statistik"));
const Aktivitaet = lazy(() => import("@/pages/crm/Aktivitaet"));
const Erstinformationen = lazy(() => import("@/pages/crm/Erstinformationen"));
const Einstellungen = lazy(() => import("@/pages/crm/Einstellungen"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const PortalLayout = lazy(() => import("@/pages/portal/PortalLayout"));
const Portal = () => import("@/pages/portal/PortalPages");
const PortalOverview = lazy(() => Portal().then((m) => ({ default: m.PortalOverview })));
const PortalVertraege = lazy(() => Portal().then((m) => ({ default: m.PortalVertraege })));
const PortalTermine = lazy(() => Portal().then((m) => ({ default: m.PortalTermine })));
const PortalSchaeden = lazy(() => Portal().then((m) => ({ default: m.PortalSchaeden })));
const PortalDokumente = lazy(() => Portal().then((m) => ({ default: m.PortalDokumente })));
const PortalAnfragen = lazy(() => Portal().then((m) => ({ default: m.PortalAnfragen })));

const STAFF = ["admin", "advisor_insurance", "advisor_finance", "advisor_realestate"];
const Loader = () => <div className="min-h-screen bg-navy-900 flex items-center justify-center text-gold-500">Lädt…</div>;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/versicherungen" element={<ServicePage areaKey="versicherung" />} />
          <Route path="/versicherungsvergleich" element={<Versicherungsvergleich />} />
          <Route path="/finanzierung" element={<ServicePage areaKey="finanzierung" />} />
          <Route path="/finanzierung/rechner" element={<Rechner />} />
          <Route path="/immobilien" element={<Immobilien />} />
          <Route path="/immobilien/:id" element={<PropertyDetail />} />
          <Route path="/anfrage/:slug" element={<Anfrage />} />
          <Route path="/ratgeber" element={<Ratgeber />} />
          <Route path="/ratgeber/:slug" element={<BlogDetail />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/ueber-uns" element={<UeberUns />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/termin-buchen" element={<Terminbuchung />} />
          <Route path="/schaden-melden" element={<Schadenmeldung />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/agb" element={<AGB />} />
          <Route path="/erstinformation" element={<Erstinformation />} />
          <Route path="/cookie-einstellungen" element={<CookieSettings />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="/login" element={<Login />} />

        <Route element={<ProtectedRoute roles={STAFF} />}>
          <Route path="/app" element={<CrmLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="kunden" element={<Kunden />} />
            <Route path="kunden/:id" element={<KundeDetail />} />
            <Route path="policen" element={<Policen />} />
            <Route path="immobilien" element={<ImmobilienAdmin />} />
            <Route path="termine" element={<Termine />} />
            <Route path="schaeden" element={<Schaeden />} />
            <Route path="aufgaben" element={<Aufgaben />} />
            <Route path="dokumente" element={<Dokumente />} />
            <Route path="erinnerungen" element={<Erinnerungen />} />
            <Route path="aktivitaet" element={<Aktivitaet />} />
            <Route path="erstinformationen" element={<Erstinformationen />} />
            <Route element={<ProtectedRoute roles={["admin"]} />}>
              <Route path="statistik" element={<Statistik />} />
              <Route path="einstellungen" element={<Einstellungen />} />
            </Route>
          </Route>
        </Route>

        <Route element={<ProtectedRoute roles={["customer"]} />}>
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalOverview />} />
            <Route path="vertraege" element={<PortalVertraege />} />
            <Route path="anfragen" element={<PortalAnfragen />} />
            <Route path="termine" element={<PortalTermine />} />
            <Route path="schaeden" element={<PortalSchaeden />} />
            <Route path="dokumente" element={<PortalDokumente />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
          <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#0A1128", border: "1px solid rgba(212,175,55,0.4)", color: "#fff" } }} />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
